import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, DatePicker, Divider, Input, Message, Modal, Panel, Stack, Tag, Tooltip, Whisper } from 'rsuite';

import {
  useProjects,
  useProjectMilestones,
  useProjectUsers,
  useCreateMilestone,
  useUpdateMilestone,
  useDeleteMilestone,
  useTaskOrders,
  useUsers,
} from '../hooks/useQueries';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const formatShortDate = (iso) => {
  const s = typeof iso === 'string' ? iso.slice(0, 10) : null;
  if (!s) return '—';
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return '—';
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return '—';
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const parseDate10Parts = (raw) => {
  const s = typeof raw === 'string' ? raw.slice(0, 10) : '';
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return { y, m: mo, d };
};

const utcDayNumber = (parts) => {
  if (!parts) return null;
  // months are 1-based in parts, 0-based in Date.UTC
  return Math.floor(Date.UTC(parts.y, parts.m - 1, parts.d) / (24 * 60 * 60 * 1000));
};

const todayParts = () => {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
};

const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

const diffFullMonths = (fromParts, toParts) => {
  if (!fromParts || !toParts) return null;
  let months = (toParts.y - fromParts.y) * 12 + (toParts.m - fromParts.m);
  if (toParts.d < fromParts.d) months -= 1;
  return Math.max(0, months);
};

const formatSpan = (fromParts, toParts) => {
  const fromDay = utcDayNumber(fromParts);
  const toDay = utcDayNumber(toParts);
  if (fromDay === null || toDay === null) return null;
  const days = toDay - fromDay;
  if (days < 0) return null;
  if (days === 0) return plural(0, 'day');

  if (days < 14) return plural(days, 'day');

  if (days < 60) {
    const weeks = Math.floor(days / 7);
    return weeks >= 2 ? plural(weeks, 'week') : plural(days, 'day');
  }

  const monthsTotal = diffFullMonths(fromParts, toParts);
  if (monthsTotal === null) return null;
  const years = Math.floor(monthsTotal / 12);
  const months = monthsTotal % 12;

  if (years > 0) return months > 0 ? `${plural(years, 'year')} ${plural(months, 'month')}` : plural(years, 'year');
  if (months > 0) return plural(months, 'month');

  const weeks = Math.floor(days / 7);
  return weeks >= 1 ? plural(weeks, 'week') : plural(days, 'day');
};

const formatDuration = (startIso, endIso) => {
  const startParts = parseDate10Parts(startIso);
  const endParts = parseDate10Parts(endIso);
  if (!startParts || !endParts) return '—';

  const startDay = utcDayNumber(startParts);
  const endDay = utcDayNumber(endParts);
  if (startDay === null || endDay === null) return '—';
  if (endDay < startDay) return '—';

  // Inclusive days is usually what humans expect for project ranges.
  const inclusiveDays = endDay - startDay + 1;

  const monthsTotal = diffFullMonths(startParts, endParts);
  const years = monthsTotal === null ? 0 : Math.floor(monthsTotal / 12);
  const months = monthsTotal === null ? 0 : monthsTotal % 12;

  if (years > 0) return months > 0 ? `${plural(years, 'year')} ${plural(months, 'month')}` : plural(years, 'year');
  if (months > 0) return plural(months, 'month');

  if (inclusiveDays >= 14) {
    const weeks = Math.floor(inclusiveDays / 7);
    const days = inclusiveDays % 7;
    return days > 0 ? `${plural(weeks, 'week')} ${plural(days, 'day')}` : plural(weeks, 'week');
  }

  return plural(inclusiveDays, 'day');
};

const formatTimeRemaining = (startIso, endIso) => {
  const endParts = parseDate10Parts(endIso);
  if (!endParts) return null;

  const startParts = parseDate10Parts(startIso);
  const today = todayParts();

  const todayDay = utcDayNumber(today);
  const startDay = startParts ? utcDayNumber(startParts) : null;
  const endDay = utcDayNumber(endParts);
  if (todayDay === null || endDay === null) return null;

  if (endDay < todayDay) return 'Ended';
  if (endDay === todayDay) return 'Ends today';

  if (startDay !== null && startDay > todayDay) {
    const span = formatSpan(today, startParts);
    return span ? `Starts in ${span}` : null;
  }

  const span = formatSpan(today, endParts);
  return span ? `${span} remaining` : null;
};

const normalizeDate10 = (raw) => {
  if (raw instanceof Date) {
    const t = raw.getTime();
    if (Number.isNaN(t)) return null;
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = typeof raw === 'string' ? raw.slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

const isoDateToDate = (raw) => {
  const iso = normalizeDate10(raw);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const out = new Date(y, m - 1, d);
  out.setHours(0, 0, 0, 0);
  return Number.isNaN(out.getTime()) ? null : out;
};

export default function ProjectDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const project = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    return (projects || []).find((p) => Number(p?.id) === projectId) ?? null;
  }, [projects, projectId]);

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();

  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [milestoneModalMode, setMilestoneModalMode] = useState('create');
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneNameInput, setMilestoneNameInput] = useState('');
  const [milestoneDateInput, setMilestoneDateInput] = useState(null);
  const [pendingDeleteMilestone, setPendingDeleteMilestone] = useState(null);

  const {
    data: projectUsersData,
    isLoading: projectUsersLoading,
    error: projectUsersError,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const projectUsers = useMemo(() => (Array.isArray(projectUsersData) ? projectUsersData : []), [projectUsersData]);

  const {
    data: milestonesData,
    isLoading: milestonesLoading,
    error: milestonesError,
  } = useProjectMilestones(projectId, { enabled: Number.isInteger(projectId) });

  const milestones = useMemo(() => {
    const rows = Array.isArray(milestonesData) ? milestonesData : [];
    return [...rows].sort((a, b) => {
      const ad = String(a?.milestone_date || '');
      const bd = String(b?.milestone_date || '');
      if (ad !== bd) return ad.localeCompare(bd);
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [milestonesData]);

  const createMilestone = useCreateMilestone();
  const updateMilestone = useUpdateMilestone();

  const deleteMilestone = useDeleteMilestone();

  const projectStartIso = useMemo(() => normalizeDate10(project?.startDate ?? project?.start_date), [project]);
  const projectEndIso = useMemo(() => normalizeDate10(project?.endDate ?? project?.end_date), [project]);

  const milestoneDateIso = useMemo(() => normalizeDate10(milestoneDateInput), [milestoneDateInput]);

  const isMilestoneInProjectRange = useMemo(() => {
    if (!milestoneDateIso) return false;
    if (projectStartIso && String(milestoneDateIso).localeCompare(String(projectStartIso)) < 0) return false;
    if (projectEndIso && String(milestoneDateIso).localeCompare(String(projectEndIso)) > 0) return false;
    return true;
  }, [milestoneDateIso, projectStartIso, projectEndIso]);

  const milestoneFormValid = useMemo(() => {
    if (!milestoneNameInput.trim()) return false;
    if (!milestoneDateIso) return false;
    if (!isMilestoneInProjectRange) return false;
    if (createMilestone.isPending || updateMilestone.isPending) return false;
    return true;
  }, [milestoneNameInput, milestoneDateIso, isMilestoneInProjectRange, createMilestone.isPending, updateMilestone.isPending]);

  const openCreateMilestoneModal = () => {
    setMilestoneModalMode('create');
    setEditingMilestone(null);
    setMilestoneNameInput('');
    setMilestoneDateInput(null);
    setMilestoneModalOpen(true);
  };

  const openEditMilestoneModal = (milestone) => {
    if (!milestone || milestone.is_auto) return;
    setMilestoneModalMode('edit');
    setEditingMilestone(milestone);
    setMilestoneNameInput(String(milestone?.name || ''));
    setMilestoneDateInput(isoDateToDate(milestone?.milestone_date));
    setMilestoneModalOpen(true);
  };

  const closeMilestoneModal = () => {
    setMilestoneModalOpen(false);
    setEditingMilestone(null);
    setMilestoneNameInput('');
    setMilestoneDateInput(null);
  };

  const saveMilestone = async () => {
    if (!Number.isInteger(projectId) || !milestoneFormValid) return;
    const payload = {
      name: milestoneNameInput.trim(),
      milestone_date: milestoneDateIso,
    };

    if (milestoneModalMode === 'edit') {
      const milestoneId = Number(editingMilestone?.id);
      if (!Number.isInteger(milestoneId)) return;
      await updateMilestone.mutateAsync({ projectId, milestoneId, payload });
      closeMilestoneModal();
      return;
    }

    await createMilestone.mutateAsync({ projectId, payload });
    closeMilestoneModal();
  };

  const confirmDeleteMilestone = async () => {
    if (!Number.isInteger(projectId) || !pendingDeleteMilestone) return;
    const milestoneId = pendingDeleteMilestone?.id;
    if (milestoneId === null || milestoneId === undefined || milestoneId === '') return;

    try {
      await deleteMilestone.mutateAsync({ projectId, milestoneId });
      setPendingDeleteMilestone(null);
    } catch {
      // error is already surfaced by mutation state/UI
    }
  };

  const allUserOptions = useMemo(() => {
    return (users || [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value));
  }, [users]);

  const projectPeopleDisplay = useMemo(() => {
    return (projectUsers || [])
      .map((u) => ({ id: Number(u?.id), name: u?.name || '—' }))
      .filter((u) => Number.isInteger(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [projectUsers]);

  const initialsForName = (raw) => {
    const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '—';
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    const s = `${first}${last}`.toUpperCase();
    return s || '—';
  };

  const avatarStyleForId = (id) => {
    const n = Number(id);
    const safe = Number.isFinite(n) ? Math.abs(Math.trunc(n)) : 0;
    const hue = (safe * 137.508) % 360;
    const background = `hsl(${hue} 70% 90%)`;
    const borderColor = `hsl(${hue} 55% 78%)`;
    return { background, borderColor };
  };

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const taskOrders = useMemo(() => (Array.isArray(taskOrdersData) ? taskOrdersData : []), [taskOrdersData]);

  const explicitTaskOrders = useMemo(() => {
    const rows = (taskOrders || []).filter((o) => !o?.isDefault);

    return rows.slice().sort((a, b) => {
      const aStart = parseDate10Parts(a?.startDate ?? a?.start_date);
      const bStart = parseDate10Parts(b?.startDate ?? b?.start_date);
      const aEnd = parseDate10Parts(a?.endDate ?? a?.end_date);
      const bEnd = parseDate10Parts(b?.endDate ?? b?.end_date);

      const aStartDay = aStart ? utcDayNumber(aStart) : null;
      const bStartDay = bStart ? utcDayNumber(bStart) : null;
      if (aStartDay !== null && bStartDay !== null && aStartDay !== bStartDay) return aStartDay - bStartDay;
      if (aStartDay !== null && bStartDay === null) return -1;
      if (aStartDay === null && bStartDay !== null) return 1;

      const aEndDay = aEnd ? utcDayNumber(aEnd) : null;
      const bEndDay = bEnd ? utcDayNumber(bEnd) : null;
      if (aEndDay !== null && bEndDay !== null && aEndDay !== bEndDay) return aEndDay - bEndDay;
      if (aEndDay !== null && bEndDay === null) return -1;
      if (aEndDay === null && bEndDay !== null) return 1;

      const aSort = Number.isFinite(Number(a?.sortOrder ?? a?.sort_order)) ? Number(a?.sortOrder ?? a?.sort_order) : null;
      const bSort = Number.isFinite(Number(b?.sortOrder ?? b?.sort_order)) ? Number(b?.sortOrder ?? b?.sort_order) : null;
      if (aSort !== null && bSort !== null && aSort !== bSort) return aSort - bSort;
      if (aSort !== null && bSort === null) return -1;
      if (aSort === null && bSort !== null) return 1;

      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [taskOrders]);
  const hasExplicitTaskOrders = explicitTaskOrders.length > 0;
  const hasOnlyProjectTaskOrder = useMemo(
    () =>
      explicitTaskOrders.length === 1
      && String(explicitTaskOrders?.[0]?.name || '').trim().toLowerCase() === 'project',
    [explicitTaskOrders]
  );

  const goToActuals = () => {
    if (!Number.isInteger(projectId)) {
      navigate('/');
      return;
    }

    const list = Array.isArray(taskOrders) ? taskOrders : [];
    if (list.length === 0) {
      navigate(`/projects/${projectId}/actuals`);
      return;
    }

    const isNamed = (o) => {
      const name = typeof o?.name === 'string' ? o.name.trim() : '';
      return name.length > 0;
    };
    const explicitNamed = list.filter((o) => !o?.isDefault && isNamed(o));
    const candidates = explicitNamed.length > 0 ? explicitNamed : list;

    // If only one candidate remains, go straight there.
    if (candidates.length === 1) {
      const onlyId = Number(candidates[0]?.id);
      if (Number.isInteger(onlyId)) {
        navigate(`/projects/${projectId}/task-orders/${onlyId}/actuals`);
        return;
      }
    }

    const todayDay = utcDayNumber(todayParts());

    const normalized = candidates
      .map((o) => {
        const id = Number(o?.id);
        if (!Number.isInteger(id)) return null;
        const startIso = o?.startDate ?? o?.start_date;
        const endIso = o?.endDate ?? o?.end_date;
        const startParts = parseDate10Parts(startIso);
        const endParts = parseDate10Parts(endIso);
        const startDay = startParts ? utcDayNumber(startParts) : null;
        const endDay = endParts ? utcDayNumber(endParts) : null;
        return { id, startDay, endDay };
      })
      .filter(Boolean);

    const lowestId = normalized.reduce((min, o) => (min === null ? o.id : Math.min(min, o.id)), null);
    if (todayDay === null || normalized.length === 0) {
      if (Number.isInteger(lowestId)) {
        navigate(`/projects/${projectId}/task-orders/${lowestId}/actuals`);
        return;
      }
      navigate(`/projects/${projectId}/actuals`);
      return;
    }

    const active = normalized.filter(
      (o) => o.startDay !== null && o.endDay !== null && o.startDay <= todayDay && todayDay <= o.endDay
    );

    if (active.length > 0) {
      // If multiple are active (overlapping), pick the one ending soonest; tiebreaker: lowest id.
      const best = active
        .slice()
        .sort((a, b) => {
          const byEnd = Number(a.endDay) - Number(b.endDay);
          if (byEnd !== 0) return byEnd;
          return a.id - b.id;
        })[0];
      navigate(`/projects/${projectId}/task-orders/${best.id}/actuals`);
      return;
    }

    const scored = normalized
      .map((o) => {
        // Prefer full ranges; then partial; then fallback.
        const hasStart = o.startDay !== null;
        const hasEnd = o.endDay !== null;
        const kind = hasStart && hasEnd ? 0 : hasStart || hasEnd ? 1 : 2;

        let dist = Number.POSITIVE_INFINITY;
        if (hasStart && hasEnd) {
          dist = todayDay < o.startDay ? o.startDay - todayDay : todayDay > o.endDay ? todayDay - o.endDay : 0;
        } else if (hasStart) {
          dist = Math.abs(o.startDay - todayDay);
        } else if (hasEnd) {
          dist = Math.abs(o.endDay - todayDay);
        }

        return { ...o, kind, dist };
      })
      .filter((o) => Number.isFinite(o.dist));

    scored.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind - b.kind;
      if (a.dist !== b.dist) return a.dist - b.dist;
      return a.id - b.id;
    });

    const best = scored[0];
    if (best && Number.isInteger(best.id)) {
      navigate(`/projects/${projectId}/task-orders/${best.id}/actuals`);
      return;
    }

    // Final fallback: lowest id.
    if (Number.isInteger(lowestId)) {
      navigate(`/projects/${projectId}/task-orders/${lowestId}/actuals`);
      return;
    }

    navigate(`/projects/${projectId}/actuals`);
  };

  const goBack = () => navigate('/', { replace: true });

  if (!Number.isInteger(projectId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>Project</h3>
        <Stack spacing={10} wrap>
          <Button appearance="ghost" size="sm" onClick={goBack}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to projects
            </span>
          </Button>
          <Button
            appearance="ghost"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/edit`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Edit
              <i className="bi bi-pencil" aria-hidden="true" />
            </span>
          </Button>
          <Button
            appearance="ghost"
            size="sm"
            onClick={goToActuals}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Actuals
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </span>
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}

      <Panel bordered header={<span>Project info</span>}>
        {projectsLoading ? <div>Loading…</div> : null}
        {!project && !projectsLoading ? (
          <Message type="warning" showIcon>
            Project not found.
          </Message>
        ) : null}

        {project ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 700 }}>Overview</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Logo</div>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      overflow: 'hidden',
                      marginTop: 4,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fafafa',
                    }}
                  >
                    {project?.photoUrl ? (
                      <img src={project.photoUrl} alt="Project" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 11, color: '#888' }}>No image</span>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Name</div>
                  <div style={{ fontWeight: 800 }}>{project?.name || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>PIC</div>
                  <div style={{ fontWeight: 700 }}>{project?.picName ?? project?.pic_name ?? 'Unassigned'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Contract Number</div>
                  <div style={{ fontWeight: 700 }}>{project?.contractNumber ?? project?.contract_number ?? '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
                  <div style={{ fontWeight: 800 }}>
                    {(project?.budgetTarget ?? project?.budget_target) === null ||
                    (project?.budgetTarget ?? project?.budget_target) === undefined
                      ? '—'
                      : formatCurrency(project?.budgetTarget ?? project?.budget_target)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 700 }}>Schedule</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Start</div>
                  <div style={{ fontWeight: 700 }}>{formatShortDate(project?.startDate ?? project?.start_date)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>End</div>
                  <div style={{ fontWeight: 700 }}>{formatShortDate(project?.endDate ?? project?.end_date)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Duration</div>
                  <div style={{ fontWeight: 800 }}>
                    {formatDuration(project?.startDate ?? project?.start_date, project?.endDate ?? project?.end_date)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Panel>

      <Panel
        bordered
        header={<span>{hasOnlyProjectTaskOrder ? 'Project' : 'Task orders'}</span>}
        style={{ marginTop: 12 }}
      >
        {taskOrdersLoading ? <div>Loading task orders…</div> : null}
        {taskOrdersError ? (
          <Message type="error" showIcon>
            Failed to load task orders.
          </Message>
        ) : null}

        {!hasExplicitTaskOrders ? (
          <Message type="info" showIcon>
            No explicit task orders for this project.
          </Message>
        ) : null}

        {(() => {
          const taskOrdersToShow = hasExplicitTaskOrders ? explicitTaskOrders : [];
          return (
            <div
              className="ppc-taskorder-grid"
              style={taskOrdersToShow.length === 1 ? { gridTemplateColumns: '1fr' } : undefined}
            >
              {taskOrdersToShow.map((o) => {
            const id = Number(o?.id);
            const name = o?.name || '—';
            const dates = o?.startDate && o?.endDate ? `${o.startDate} → ${o.endDate}` : null;
            const budget = o?.budgetTarget === null || o?.budgetTarget === undefined ? null : formatCurrency(o.budgetTarget);
            const remaining = formatTimeRemaining(o?.startDate ?? o?.start_date, o?.endDate ?? o?.end_date);

            const goTo = () => {
              if (!Number.isInteger(id)) return;
              navigate(`/projects/${projectId}/task-orders/${id}`);
            };

            return (
              <div
                key={String(id)}
                className="ppc-taskorder-card"
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: '#fbfbfc',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 122,
                }}
                role="button"
                tabIndex={0}
                onClick={goTo}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goTo();
                  }
                }}
              >
                {remaining ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 10,
                      fontSize: 11,
                      color: remaining === 'Ended' ? '#a13a3a' : '#666',
                      fontWeight: 700,
                      pointerEvents: 'none',
                    }}
                  >
                    {remaining}
                  </div>
                ) : null}

                <div style={{ textAlign: 'center', padding: '4px 8px', minWidth: 0 }}>
                  <div style={{ fontWeight: 800, lineHeight: '18px' }}>{name}</div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    {dates ? dates : 'Dates not set'}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {budget ? `Budget ${budget}` : 'Budget —'}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      appearance="primary"
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        goTo();
                      }}
                      disabled={!Number.isInteger(id)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        View Task Order
                        <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            );
              })}
            </div>
          );
        })()}
      </Panel>

      <Panel
        bordered
        header={<span>People</span>}
        style={{ marginTop: 12 }}
      >
        {projectUsersError ? (
          <Message type="error" showIcon>
            Failed to load project people.
          </Message>
        ) : null}

        {projectUsersLoading ? <div style={{ color: '#666', fontSize: 12 }}>Loading people…</div> : null}
        {projectPeopleDisplay.length === 0 && !projectUsersLoading ? (
          <Message type="info" showIcon>
            No one is assigned to this project yet.
          </Message>
        ) : null}

        {projectPeopleDisplay.length > 0 ? (
          <div className="ppc-people-grid">
            {projectPeopleDisplay.map((p) => (
              <div key={p.id} className="ppc-person-card">
                <span
                  className="ppc-person-avatar"
                  style={(() => {
                    const s = avatarStyleForId(p.id);
                    return { background: s.background, borderColor: s.borderColor };
                  })()}
                >
                  {initialsForName(p.name)}
                </span>
                <div style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'center' }}>
                  <div style={{ fontWeight: 800, lineHeight: '18px' }}>{p.name}</div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {usersError ? (
          <Message type="error" showIcon>
            Failed to load users.
          </Message>
        ) : null}
        {usersLoading && allUserOptions.length === 0 ? (
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>User directory is still loading.</div>
        ) : null}
      </Panel>

      <Panel bordered header="Milestones" style={{ marginTop: 12 }}>
        {milestonesError ? (
          <Message type="error" showIcon>
            Failed to load milestones.
          </Message>
        ) : null}

        {milestonesLoading ? <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>Loading milestones…</div> : null}

        {!milestonesLoading && milestones.length === 0 ? (
          <Message type="info" showIcon>
            No milestones yet.
          </Message>
        ) : null}

        {milestones.length > 0 ? (
          <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
            {milestones.map((m) => {
              const isAuto = Boolean(m?.is_auto);
              const milestoneId = m?.id;
              return (
                <div
                  key={String(milestoneId)}
                  className="ppc-milestone-row"
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(m?.name || 'Milestone')}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>{formatShortDate(m?.milestone_date)}</div>
                  </div>

                  <div className="ppc-milestone-row__actions">
                    {isAuto ? (
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>This milestone was automatically generated from task order dates</Tooltip>}
                      >
                        <Tag color="blue" size="sm">Auto</Tag>
                      </Whisper>
                    ) : null}

                    {!isAuto ? (
                      <Button size="xs" appearance="subtle" onClick={() => openEditMilestoneModal(m)}>
                        Edit
                      </Button>
                    ) : null}

                    <Button
                      size="xs"
                      appearance="subtle"
                      color="red"
                      loading={deleteMilestone.isPending}
                      onClick={() => {
                        setPendingDeleteMilestone({
                          id: milestoneId,
                          name: String(m?.name || 'Milestone'),
                        });
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {createMilestone.error ? (
          <Message type="error" showIcon style={{ marginBottom: 8 }}>
            {String(createMilestone.error?.message || 'Failed to add milestone.')}
          </Message>
        ) : null}

        {updateMilestone.error ? (
          <Message type="error" showIcon style={{ marginBottom: 8 }}>
            {String(updateMilestone.error?.message || 'Failed to update milestone.')}
          </Message>
        ) : null}

        {!isMilestoneInProjectRange && milestoneDateIso ? (
          <Message type="warning" showIcon style={{ marginBottom: 8 }}>
            Milestone date must be within the project range.
          </Message>
        ) : null}

        <div className="ppc-milestone-add-wrap">
          <Button appearance="primary" size="sm" onClick={openCreateMilestoneModal}>
            Add milestone
          </Button>
        </div>

        <Modal open={milestoneModalOpen} onClose={closeMilestoneModal} size="xs">
          <Modal.Header>
            <Modal.Title>{milestoneModalMode === 'edit' ? 'Edit milestone' : 'Add milestone'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Name</div>
                <Input value={milestoneNameInput} onChange={setMilestoneNameInput} placeholder="Milestone name" />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Date</div>
                <DatePicker
                  oneTap
                  cleanable={false}
                  format="MM/dd/yyyy"
                  value={isoDateToDate(normalizeDate10(milestoneDateInput))}
                  onChange={(d) => setMilestoneDateInput(normalizeDate10(d) ?? '')}
                  style={{ width: '100%' }}
                  shouldDisableDate={(date) => {
                    const iso = normalizeDate10(date);
                    if (!iso) return false;
                    if (projectStartIso && String(iso).localeCompare(String(projectStartIso)) < 0) return true;
                    if (projectEndIso && String(iso).localeCompare(String(projectEndIso)) > 0) return true;
                    return false;
                  }}
                />
              </div>

              {!isMilestoneInProjectRange && milestoneDateIso ? (
                <Message type="warning" showIcon>
                  Milestone date must be within the project range.
                </Message>
              ) : null}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="primary"
              onClick={() => void saveMilestone()}
              loading={createMilestone.isPending || updateMilestone.isPending}
              disabled={!milestoneFormValid}
            >
              Save
            </Button>
            <Button appearance="subtle" onClick={closeMilestoneModal}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          open={Boolean(pendingDeleteMilestone)}
          onClose={() => {
            if (deleteMilestone.isPending) return;
            setPendingDeleteMilestone(null);
          }}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Delete milestone?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              This will permanently delete <strong>{pendingDeleteMilestone?.name || 'this milestone'}</strong>.
            </Message>
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="primary"
              color="red"
              loading={deleteMilestone.isPending}
              disabled={deleteMilestone.isPending}
              onClick={() => void confirmDeleteMilestone()}
            >
              Delete
            </Button>
            <Button
              appearance="subtle"
              disabled={deleteMilestone.isPending}
              onClick={() => setPendingDeleteMilestone(null)}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </Panel>
    </div>
  );
}
